import Button from '@ui/Button'
import Field from '@ui/Field'
import Input from '@ui/Input'
import ProviderButton from '@ui/ProviderButton'
import type { MultiFactorResolver, User } from 'firebase/auth'
import { type FormEvent, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import {
  type EmailSendStatus,
  IdpError,
  type IdpErrorCode,
  reauthWithGoogle,
  resolveTotp,
  sendPasswordReset,
  signInWithPassword,
} from '@/auth/idp'

import OneTimeCodeField from './OneTimeCodeField'

export type ReauthMethod = 'password' | 'google'

type Props = {
  email: string
  intro: string
  /** How the user proves it's them: their password, or Google again. */
  method?: ReauthMethod
  passwordLabel?: string
  /** Runs with the freshly signed-in Firebase user; a thrown IdpError shows inline. */
  onVerified: (user: User) => Promise<void>
}

/** Sheet copy for errors whose login-page wording would mislead here. */
const SHEET_ERROR_KEY: Partial<Record<IdpErrorCode, string>> = {
  'recent-login-required': 'two-factor.errors.recent-login-required',
  'user-mismatch': 'profile.errors.user-mismatch',
  'mfa-expired': 'auth.totp.expired',
}

export const idpErrorKey = (code: IdpErrorCode) =>
  SHEET_ERROR_KEY[code] ?? `auth.password.errors.${code}`

/**
 * Identity Platform only lets a freshly signed-in user change a password or a
 * second factor, so sheets that do either start here: password, then the
 * authenticator code if 2FA is on.
 */
const ReauthForm = ({
  email,
  intro,
  method = 'password',
  passwordLabel,
  onVerified,
}: Props) => {
  const { t } = useTranslation()
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [resolver, setResolver] = useState<MultiFactorResolver | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<IdpErrorCode | null>(null)
  const [resetStatus, setResetStatus] = useState<EmailSendStatus | null>(null)

  const run = async (work: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await work()
    } catch (err) {
      const code = err instanceof IdpError ? err.code : 'unknown'
      setError(code === 'cancelled' ? null : code)
    } finally {
      setBusy(false)
    }
  }

  const onPassword = (e: FormEvent) => {
    e.preventDefault()
    run(async () => {
      const result = await signInWithPassword(email, password)
      if (result.kind === 'totp-required') {
        setResolver(result.resolver)
        return
      }
      await onVerified(result.user)
    })
  }

  const onCode = (e: FormEvent) => {
    e.preventDefault()
    if (!resolver) return
    run(async () => {
      await onVerified(await resolveTotp(resolver, code.trim()))
    })
  }

  const onGoogle = () => {
    run(async () => {
      const result = await reauthWithGoogle(email)
      if (result.kind === 'totp-required') {
        setResolver(result.resolver)
        return
      }
      await onVerified(result.user)
    })
  }

  const onForgot = () => {
    run(async () => {
      setResetStatus(await sendPasswordReset(email))
    })
  }

  const errorLine = error && (
    <p role="alert" className="text-sm text-flag">
      {t(idpErrorKey(error), { email })}
    </p>
  )

  if (resetStatus) {
    return (
      <p className="text-sm text-ink-700">
        <Trans
          i18nKey={
            resetStatus === 'recently-sent'
              ? 'auth.check-email.reset-recently-sent-body'
              : 'auth.check-email.reset-body'
          }
          values={{ email }}
          components={{ strong: <strong /> }}
        />
      </p>
    )
  }

  if (resolver) {
    return (
      <form key="code" onSubmit={onCode} className="flex flex-col gap-4">
        <p className="text-sm text-ink-700">{t('auth.password.totp-intro')}</p>
        <OneTimeCodeField value={code} onChange={setCode} />
        {errorLine}
        <Button type="submit" fullWidth disabled={busy || code.length < 6}>
          {t('auth.password.verify')}
        </Button>
      </form>
    )
  }

  if (method === 'google') {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-ink-700">{intro}</p>
        {errorLine}
        <ProviderButton provider="google" disabled={busy} onClick={onGoogle} />
      </div>
    )
  }

  return (
    <form key="password" onSubmit={onPassword} className="flex flex-col gap-4">
      <p className="text-sm text-ink-700">{intro}</p>
      <Field label={passwordLabel ?? t('auth.password.password')}>
        <Input
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
          required
        />
      </Field>
      {errorLine}
      <Button type="submit" fullWidth disabled={busy}>
        {t('two-factor.continue')}
      </Button>
      <Button variant="ghost" fullWidth disabled={busy} onClick={onForgot}>
        {t('auth.password.forgot')}
      </Button>
    </form>
  )
}

export default ReauthForm
